import {
  BottomBar,
  DurationPicker,
  Input,
  ISelectOption,
  SaveBar,
  Select,
  Spinner,
  Form,
  Alert,
} from '@salecycle/ui-component-library';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import { IDuration, IUnit, millisToDuration, unitToMillis } from 'core/ui/utils/unitMillis';
import { TFunction } from 'i18next';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { generatePath } from 'react-router';
import { useHistory } from 'react-router-dom';
import { ClientRoute } from 'sections/client-management/routes/ClientRoute';
import { THIRTY_DAYS_IN_MILLISECONDS } from 'utils/constants';
import { SiteSettingsApiEndpoints } from '../../utils/api-endpoints';
import { useFrequencyCapsDispatch, useFrequencyCapsState } from '../context/FrequencyCapsContext';
import { ICreateFrequencyCapRequest, IFrequencyCap, IUpdateFrequencyCapRequest } from '../entities/frequency-caps';
import { StyledSpinnerContainer } from './styles';
import { getFrequencyCapList } from '../utils/frequencyCapUtils';
type DurationUnitOptions = { label: string; value: IUnit }[];

const durationUnitOptions = (t: TFunction): DurationUnitOptions => [
  { label: t('settings.frequencyCaps.interval.unitOptions.hours'), value: 'hours' },
  { label: t('settings.frequencyCaps.interval.unitOptions.days'), value: 'days' },
  { label: t('settings.frequencyCaps.interval.unitOptions.weeks'), value: 'weeks' },
];

interface IFormProps {
  heading: string;
  frequencyCapId?: string;
  siteId: string;
  closeModal?: () => void;
  setFrequencyCaps?: Dispatch<SetStateAction<IFrequencyCap[]>>;
  savedFrequencyCaps?: IFrequencyCap[];
  editFrequencyCap?: boolean;
  currentFrequencyCap?: string;
  currentFrequencyCapPeriod?: number;
  currentFrequencyCapAmount?: number;
}

const FrequencyCapForm: FC<IFormProps> = props => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const { showNotification } = useNotifications();
  const dispatch = useFrequencyCapsDispatch();
  const { frequencyCap, originalFrequencyCap } = useFrequencyCapsState();
  const [isSaving, setIsSaving] = useState(false);
  const [maximumDisplay, setMaximumDisplay] = useState<string>(
    props.editFrequencyCap ? props.currentFrequencyCapAmount.toString() : frequencyCap.amount.toString(),
  );
  const [interval, setInterval] = useState<IDuration>(
    millisToDuration(props.editFrequencyCap ? props.currentFrequencyCapPeriod : frequencyCap.period),
  );
  const frequencyCapList = getFrequencyCapList();
  const [frequencyCapOption, setFrequencyCapOptions] = useState(filteredList()[0]);

  const putFrequencyCapsMutation = useMutation(
    async (frequencyCap: IUpdateFrequencyCapRequest) =>
      RestApi.put(
        generatePath(SiteSettingsApiEndpoints.PUT_FREQ_CAPS, { siteId: props.siteId, frequencyCapId: frequencyCap.id }),
        frequencyCap,
      ),
    {
      onSuccess: (data, variables, context) => {
        showNotification(`Frequency cap updated`, 'normal');
        props.setFrequencyCaps(freq =>
          freq.map(cap => (cap.id === data.data['id'] ? (data.data as IFrequencyCap) : cap)),
        );
      },
      onError: (error: RestApiError, variables, context) => {
        showNotification(`Unable to update frequency cap`, 'error');
      },
    },
  );

  const postFrequencyCapsMutation = useMutation(
    async (frequencyCap: ICreateFrequencyCapRequest) =>
      RestApi.post(generatePath(SiteSettingsApiEndpoints.POST_FREQ_CAPS, { siteId: props.siteId }), frequencyCap),
    {
      onSuccess: (data, variables, context) => {
        showNotification(`Frequency cap created`, 'normal');
        props.setFrequencyCaps(freq => [...freq, data.data as IFrequencyCap]);
      },
      onError: (error: RestApiError, variables, context) => {
        showNotification(`Unable to create frequency cap`, 'error');
      },
    },
  );

  const frequencyCapOptionHandler = (option: ISelectOption<string>) => {
    setFrequencyCapOptions(option);
  };

  const handleCreateFrequencyCaps = async (frequencyCaps: ICreateFrequencyCapRequest) => {
    postFrequencyCapsMutation.mutate(frequencyCaps);
  };

  const handleUpdateFrequencyCaps = async (frequencyCaps: IUpdateFrequencyCapRequest) => {
    putFrequencyCapsMutation.mutate(frequencyCaps);
  };

  const onMaxMessagesChange = (value: string) => {
    setMaximumDisplay(value);
    dispatch({
      type: 'UPDATE_MAX_MESSAGES',
      payload: {
        maximum: Number(value),
      },
    });
  };

  const onIntervalChange = (interval: IDuration) => {
    setInterval(interval);
    dispatch({
      type: 'UPDATE_INTERVAL',
      payload: {
        interval: unitToMillis(interval.value, interval.unit),
      },
    });
  };

  function isInvalidMaximumMessages(): boolean {
    return props.currentFrequencyCap === 'abandonmentsPerPeriod'
      ? isNaN(parseInt(maximumDisplay)) || parseInt(maximumDisplay) < 1
      : false;
  }

  const isInvalidDuration = () => {
    return interval.value === undefined || interval.value <= 0 || isOutsideRetentionPeriod();
  };

  const isOutsideRetentionPeriod = (): boolean => {
    return unitToMillis(interval.value, interval.unit as IUnit) > THIRTY_DAYS_IN_MILLISECONDS;
  };

  function filteredList(): ISelectOption<string>[] {
    const savedCap = props.savedFrequencyCaps && props.savedFrequencyCaps[0].type;
    // only need additional logic if both caps are visible
    if (frequencyCapList.length > 1) {
      if (props.editFrequencyCap) {
        return frequencyCapList
          .filter(cap => cap.value === props.currentFrequencyCap)
          .map(cap => ({
            value: cap.value,
            label: cap.label,
          }));
      } else if (savedCap && !props.editFrequencyCap) {
        return frequencyCapList.filter(cap => cap.value !== savedCap);
      }
    }
    return frequencyCapList;
  }

  const getDurationValidationMessage = (): string => {
    if (interval.value === undefined) {
      return t('settings.frequencyCaps.interval.validation.required');
    }
    if (interval.value <= 0) {
      return t('settings.frequencyCaps.interval.validation.min');
    }
    if (isOutsideRetentionPeriod()) {
      return t('settings.frequencyCaps.interval.validation.max');
    }
    return '';
  };

  const getMaxMessagesValidationMessage = (): string => {
    if (maximumDisplay === undefined) {
      return t(`settings.frequencyCaps.edit.types.${frequencyCapOption.value}.amount.validation.required`);
    }
    if (isInvalidMaximumMessages()) {
      return t(`settings.frequencyCaps.edit.types.${frequencyCapOption.value}.amount.validation.min`);
    }
    return '';
  };

  const save = async () => {
    setIsSaving(true);
    const frequencyCapAmount = frequencyCapOption.value === 'abandonmentsPerPeriod' ? Number(maximumDisplay) : null;
    const frequencyPeriod = unitToMillis(interval.value, interval.unit);
    if (props.savedFrequencyCaps && props.editFrequencyCap) {
      const currentFrequencyCap = props.savedFrequencyCaps.filter(cap => cap.type === frequencyCapOption.value);
      await handleUpdateFrequencyCaps({
        id: currentFrequencyCap[0].id,
        type: frequencyCapOption.value,
        amount: frequencyCapAmount,
        period: frequencyPeriod,
      } as IUpdateFrequencyCapRequest);
    } else {
      await handleCreateFrequencyCaps({
        type: frequencyCapOption.value,
        amount: frequencyCapAmount,
        period: frequencyPeriod,
      } as ICreateFrequencyCapRequest);
    }
    props.closeModal();
    setIsSaving(false);
  };

  const returnToListPage = () => {
    push(generatePath(ClientRoute.FREQ_CAPS_LIST, { siteId: props.siteId }));
    props.closeModal();
  };

  const onReset = (): void => {
    setMaximumDisplay(originalFrequencyCap.amount.toString());
    setInterval(millisToDuration(originalFrequencyCap.period));
    dispatch({
      type: 'RESET',
    });
  };

  const enableSave = (): boolean => {
    return (
      (props.frequencyCapId === undefined || JSON.stringify(originalFrequencyCap) !== JSON.stringify(frequencyCap)) &&
      !isSaving &&
      !isInvalidDuration() &&
      !isInvalidMaximumMessages()
    );
  };

  return (
    <div data-test-id="FrequencyCapForm">
      <Form>
        {isSaving && (
          <StyledSpinnerContainer>
            <Spinner />
          </StyledSpinnerContainer>
        )}

        <Select
          ariaLabel={t(`settings.frequencyCaps.list.capTypeHeader`)}
          label={t(`settings.frequencyCaps.list.capTypeHeader`)}
          options={filteredList()}
          value={frequencyCapOption}
          onChange={frequencyCapOptionHandler}
          tooltipDescription={[
            t(`settings.frequencyCaps.edit.types.tooltipDescriptionP1`),
            t(`settings.frequencyCaps.edit.types.tooltipDescriptionP2`),
          ]}
          tooltipHeading={t(`settings.frequencyCaps.edit.types.tooltipHeader`)}
        />
        {frequencyCapOption.value === 'abandonmentsPerPeriod' && (
          <>
            <Input
              value={maximumDisplay}
              type="number"
              onChange={value => onMaxMessagesChange(value)}
              label={t(`settings.frequencyCaps.edit.types.${frequencyCapOption.value}.amount.label`)}
              ariaLabel={t(`settings.frequencyCaps.edit.types.${frequencyCapOption.value}.amount.label`)}
              validationState={isInvalidMaximumMessages() ? 'warning' : null}
              validationMessage={getMaxMessagesValidationMessage()}
              tooltipDescription={[
                t(`settings.frequencyCaps.edit.types.abandonmentsPerPeriod.tooltipDescriptionP1`),
                t(`settings.frequencyCaps.edit.types.abandonmentsPerPeriod.tooltipDescriptionP2`),
              ]}
              tooltipHeading={t(`settings.frequencyCaps.edit.types.abandonmentsPerPeriod.tooltipHeader`)}
            />
            <Alert variant="info">
              {t('settings.frequencyCaps.list.types.abandonmentsPerPeriod.alert')}
              <a
                href={t('settings.frequencyCaps.list.types.abandonmentsPerPeriod.link')}
                target="_blank"
                rel="noreferrer"
              >
                {t('settings.frequencyCaps.list.types.abandonmentsPerPeriod.linkLabel')}
              </a>
            </Alert>
          </>
        )}

        <DurationPicker
          value={interval.value}
          onChange={(value, unit) => onIntervalChange({ value, unit } as IDuration)}
          label={t('settings.frequencyCaps.interval.label')}
          unit={interval.unit}
          ariaLabel={t('settings.frequencyCaps.interval.ariaLabel')}
          unitAriaLabel={t('settings.frequencyCaps.interval.unitAriaLabel')}
          units={durationUnitOptions(t)}
          validationState={isInvalidDuration() ? 'warning' : null}
          validationMessage={getDurationValidationMessage()}
        />
      </Form>
      <BottomBar>
        <SaveBar
          onDiscardButtonClicked={() => onReset()}
          onSaveButtonClicked={() => save()}
          onExitButtonClicked={() => returnToListPage()}
          enableSaveButton={enableSave()}
        />
      </BottomBar>
    </div>
  );
};

export default FrequencyCapForm;
